<template>
    <div class="space-y-6">
        <header class="mb-6">
            <Heading
                :level="1"
                title="Settings Management"
                subtitle="Manage settings for your organization."
            />
        </header>

        <Card class="rounded">
            <CardHeader>
                <CardTitle>System Settings</CardTitle>
                <p class="text-sm text-muted-foreground">
                    These are default system settings for your organization.
                </p>
            </CardHeader>
            <CardContent class="grid grid-cols-12 gap-4">
                <div class="col-span-12 xl:col-span-6 space-y-4">
                    <div>
                        <label class="block text-sm font-medium mb-1">Default platform</label>
                        <DropdownSelect
                            v-model="editedSettings.default_platform_id"
                            class="w-full"
                            @update:model-value="onInputChange"
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select a platform">
                                    {{ platforms.find((item) => item.id === editedSettings.default_platform_id)?.label }}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem :value="null">None</SelectItem>
                                    <SelectItem
                                        v-for="platform in platforms"
                                        :key="platform.id"
                                        :value="platform.id"
                                    >
                                        {{ platform.label }}
                                    </SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </DropdownSelect>
                        <p class="text-xs mt-1">
                            Sets the default platform used for filtering across the system.
                        </p>
                    </div>

                    <div v-if="isSfStaff">
                        <label class="block text-sm font-medium mb-1">Subscription plan</label>
                        <DropdownSelect
                            v-model="editedSettings.tier"
                            :disabled="selfHasSubscription"
                            class="w-full"
                            @update:model-value="onInputChange"
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select a plan">
                                    {{ subscriptionPlans.find((item) => item.id === editedSettings.tier)?.label }}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem :value="null">None</SelectItem>
                                    <SelectItem
                                        v-for="plan in subscriptionPlans"
                                        :key="plan.id"
                                        :value="plan.id"
                                    >
                                        {{ plan.label }}
                                    </SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </DropdownSelect>
                        <p class="text-xs mt-1">
                            Used to set the organization’s plan when invoiced directly.
                        </p>
                    </div>

                    <div v-if="isSfStaff">
                        <label class="block text-sm font-medium mb-1">Enable Discovery Instagram Audience filters</label>
                        <div class="form-switch">
                            <input
id="discovery_instagram_audience_filters"
                                v-model="editedSettings.discovery_instagram_audience_filters"
                                type="checkbox"
                                class="sr-only"
                                 @change="onInputChange"
                            />
                            <label class="bg-slate-400" for="discovery_instagram_audience_filters">
                                <span class="bg-white shadow-sm" aria-hidden="true"></span>
                                <span class="sr-only">Toggle</span>
                            </label>
                        </div>

                        <p class="text-xs mt-1">
                            Used to enable audience filters within Discovery for Instagram
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>

        <Card class="rounded">
            <CardHeader>
                <CardTitle>Email Settings</CardTitle>
                <p class="text-sm text-muted-foreground">
                    These are default email settings for your organization.
                </p>
            </CardHeader>
            <CardContent class="grid grid-cols-12 gap-4">
                <div class="col-span-12 xl:col-span-6 space-y-4">
                    <div>
                        <label class="block text-sm font-medium mb-1">Default sender name</label>
                        <input
                            v-model="editedSettings.sender_name"
                            class="form-input w-full"
                            type="text"
                            maxlength="200"
                            @input="onInputChange('organization-settings')"
                        />
                        <p class="text-xs mt-1">
                            This will auto populate the sender name field when sending outreach.
                        </p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1">Default sender email address</label>
                        <Tooltip placement="bottom" max-width="400" class="cursor-pointer">
                            <template #trigger>
                                <div class="form-input w-full bg-slate-100 hover:cursor-default hover:border-slate-200">
                                    campaigns@streamforge.gg
                                </div>
                            </template>
                            <template #default>
                                <p class="text-sm">Customizing this is currently not available.</p>
                            </template>
                        </Tooltip>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1">Default reply-to name</label>
                        <input
                            v-model="editedSettings.reply_name"
                            class="form-input w-full"
                            type="text"
                            maxlength="200"
                            @input="onInputChange('organization-settings')"
                        />
                        <p class="text-xs mt-1">
                            Often the same as the sender name.
                        </p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1">Default reply-to email address</label>
                        <input
                            v-model="editedSettings.reply_address"
                            class="form-input w-full"
                            type="text"
                            maxlength="200"
                            @input="onInputChange('organization-settings')"
                        />
                        <p class="text-xs mt-1">
                            This can be your own individual email, or a group email set up by your company.
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>

        <div class="flex justify-end space-x-2">
            <Button
                variant="outline"
                size="sm"
                :disabled="!state.settings.settingsChanged"
                @click="cancelChanges()"
            >
                Cancel
            </Button>

            <Button
                variant="default"
                size="sm"
                @click="saveOrganizationSettings()"
            >
                Save Settings
            </Button>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import organizationAPI from '@/utils/api/api.organization'
import verificationAPI from '@/utils/api/api.verification'
import { Heading } from '@/components/ui/typography'
import Tooltip from '@/components/Tooltip.vue'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export default {
    name: 'OrganizationSettingsSubpage',

    components: {
        Tooltip,
        Heading,
        DropdownSelect: Select,
        SelectContent,
        SelectGroup,
        SelectItem,
        SelectTrigger,
        SelectValue,
        Card,
        CardContent,
        CardHeader,
        CardTitle,
        Button,
    },

    props: {
        organization: {
            type: Object,
        },
    },

    data() {
        return {
            settings: {},
            editedSettings: {},

            state: {
                settings: {
                    isLoaded: false,
                    settingsChanged: false,
                },
            },

            platforms: [],
            subscriptionPlans: [],
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'isSfStaff', 'selfActiveOrganizationId', 'selfHasSubscription']),
    },

    created() {
        this.state.settings.isLoaded = false
        this.getOrganizationSettings()

        this.platforms = ['instagram', 'tiktok', 'youtube', 'twitch', 'twitter'].map((platformValueId) => {
            return {
                id: platformValueId,
                label: this.getFromDictionary(`platforms.toProper.${platformValueId}`),
            }
        })

        this.subscriptionPlans = Object.entries(this.getFromDictionary('products'))
            .map(([key, value]) => ({
                id: key,
                label: value.label,
            }))
    },

    methods: {
        async getOrganizationSettings() {
            const result = await organizationAPI.getOrganizationSettings(this.$axios, this.selfActiveOrganizationId)
            if (result.success && result.value.settings) {
                this.editedSettings = result.value.settings
                this.settingsUnedited = _.cloneDeep(this.editedSettings)
            } else {
                this.editedSettings = {}
                this.settingsUnedited = {}
            }
        },
        onInputChange() {
            this.state.settings.settingsChanged = true
        },
        cancelChanges() {
            this.editedSettings = _.cloneDeep(this.settingsUnedited)
            this.state.settings.settingsChanged = false
        },
        async validateChanges() {
            const newReplyToEmail = _.get(this.editedSettings, 'reply_address')
            const oldReplyToEmail = _.get(this.settingsUnedited, 'reply_address')

            if (newReplyToEmail && (!oldReplyToEmail || oldReplyToEmail !== newReplyToEmail)) {
                const result = await verificationAPI.validateEmail(this.$axios, newReplyToEmail)
                const isValid = result.success && result.value.is_success
                return {
                    isValid,
                    reason: isValid ? '' : 'Please enter a valid reply email address',
                }
            }

            return {
                isValid: true,
            }
        },
        async saveOrganizationSettings() {
            const valididated = await this.validateChanges()
            if (valididated.isValid) {
                const result = await organizationAPI.saveOrganizationSettings(
                    this.$axios,
                    this.selfActiveOrganizationId,
                    this.editedSettings,
                )
                if (result && result.success) {
                    this.state.settings.settingsChanged = false

                    this.$store.commit('self:defaultPlatformId', {
                        defaultPlatformId: this.editedSettings.default_platform_id,
                    })
                    this.$store.commit('self:subscriptionTier', {
                        subscriptionTier: this.editedSettings.tier,
                    })

                    this.$successHandler('Successfully saved organization settings')
                } else {
                    const message = 'Unable to save settings, please try again later'
                    this.$errorHandler(message, { title: 'Error saving settings', message })
                }
            } else {
                this.$errorHandler(valididated.reason, { title: 'Error saving settings', message: valididated.reason })
            }
        },
    },
}
</script>
